import { useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { useAppStore } from "@/store";
import { useDebounce } from "./use-debounce";
import { formScheduleKeys } from "./use-form-schedules";

type FormSchedulePreviewResponse = components["schemas"]["FormSchedulePreviewResponse"];

/** Coalesce builder keystrokes/clicks so the server sees one settled rule. */
const PREVIEW_DEBOUNCE_MS = 400;
/** The preview panel shows 5 dates; the review step reuses the same cache. */
const PREVIEW_COUNT = 5;
const PREVIEW_STALE_TIME = 60_000;

export interface OccurrencePreviewInput {
  rrule: string;
  startDate: string;
  endDate: string | null;
}

export interface OccurrencePreviewResult {
  occurrences: string[];
  hasMore: boolean;
  openEnded: boolean;
  isPending: boolean;
  /** Inputs changed but the 400ms settle window has not elapsed yet. */
  isDebouncing: boolean;
  /** Verbatim engine message from the 422 `invalid_rrule` envelope, else null. */
  invalidRuleMessage: string | null;
  isError: boolean;
  /** Re-fires the preview after a network failure (the panel's Retry link). */
  refetch: () => void;
}

/**
 * Debounced server-side occurrence preview for the recurrence builder.
 * POSTs the stateless preview endpoint with the settled rrule + start_date +
 * end_date triple; `keepPreviousData` keeps the date list from flickering
 * empty while a changed rule recomputes. A 422 is a terminal answer about the
 * rule (never transient), so `retry: false` and the engine message surfaces
 * through `invalidRuleMessage` for the preview panel's danger state.
 */
export function useOccurrencePreview({
  rrule,
  startDate,
  endDate,
}: OccurrencePreviewInput): OccurrencePreviewResult {
  const orgId = useAppStore((s) => s.activeOrgId);

  // The three inputs debounce as ONE unit (a single serialized tuple) so a
  // burst of builder edits settles into exactly one preview request — three
  // independent timers could fire mid-burst with mismatched combinations.
  const serialized = JSON.stringify([rrule, startDate, endDate]);
  const debouncedSerialized = useDebounce(serialized, PREVIEW_DEBOUNCE_MS);
  const isDebouncing = serialized !== debouncedSerialized;
  const [debouncedRrule, debouncedStartDate, debouncedEndDate] = useMemo(
    // Cast restores the tuple shape this hook serialized two statements up.
    () => JSON.parse(debouncedSerialized) as [string, string, string | null],
    [debouncedSerialized],
  );

  const query = useQuery<FormSchedulePreviewResponse>({
    queryKey: [
      ...formScheduleKeys.all,
      "preview",
      orgId ?? "",
      debouncedRrule,
      debouncedStartDate,
      debouncedEndDate,
    ],
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST(
        "/orgs/{orgId}/form-schedules/preview-occurrences",
        {
          params: { path: { orgId } },
          body: {
            rrule: debouncedRrule,
            start_date: debouncedStartDate,
            end_date: debouncedEndDate,
            count: PREVIEW_COUNT,
          },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to preview occurrences";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId) && debouncedRrule !== "" && debouncedStartDate !== "",
    staleTime: PREVIEW_STALE_TIME,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const invalidRuleMessage = useMemo(() => {
    const err = query.error;
    if (!(err instanceof ApiError) || err.status !== 422) return null;
    // The 422 envelope carries the engine text verbatim in `message`; the
    // `error` field is the machine token (`invalid_rrule`).
    const details = err.details as { message?: string } | undefined;
    return details?.message ?? err.message;
  }, [query.error]);

  return {
    occurrences: query.data?.occurrences ?? [],
    hasMore: query.data?.has_more ?? false,
    openEnded: query.data?.open_ended ?? false,
    isPending: query.isPending,
    isDebouncing,
    invalidRuleMessage,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
